import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Search, ShoppingBag, LogOut } from "lucide-react";
import { Toaster, toast } from "sonner";
import StickyPlayer from "@/components/StickyPlayer";
import { api } from "@/lib/api";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/songs", label: "Songs" },
  { to: "/videos", label: "Videos" },
  { to: "/albums", label: "Albums" },
  { to: "/store", label: "Store" },
  { to: "/library", label: "Library" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Layout({ children }) {
  const loc = useLocation();
  const hideChrome = loc.pathname.startsWith("/payment") || loc.pathname.startsWith("/admin");
  const [me, setMe] = useState(null);

  useEffect(() => {
    if (hideChrome) return;
    let cancelled = false;
    api.get("/auth/me")
      .then((r) => { if (!cancelled) setMe(r.data); })
      .catch(() => { if (!cancelled) setMe(null); });
    return () => { cancelled = true; };
  }, [hideChrome, loc.pathname]);

  const signOut = async (e) => {
    e.preventDefault(); e.stopPropagation();
    try { await api.post("/auth/logout"); } catch (_) {}
    localStorage.removeItem("sanctum_token");
    setMe(null);
    toast.success("Signed out");
  };

  const initial = me?.email ? me.email[0].toUpperCase() : "";

  return (
    <div className="min-h-screen flex flex-col relative">
      {!hideChrome && (
        <header className="header-glass fixed top-0 inset-x-0 z-40">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
            <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
              <span className="font-display text-2xl tracking-tight">Sanctum</span>
              <span className="eyebrow hidden sm:inline">Music</span>
            </Link>
            <nav className="hidden md:flex items-center gap-8">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={`nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `text-sm tracking-wide transition-colors ${
                      isActive ? "text-neutral-900" : "text-neutral-500 hover:text-neutral-900"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>
            <div className="flex items-center gap-2">
              <Link to="/songs" data-testid="nav-search" className="p-2 rounded-full hover:bg-neutral-100">
                <Search size={18} strokeWidth={1.5} />
              </Link>
              <Link to="/store" data-testid="nav-store-icon" className="btn-pill btn-primary !py-2 !px-4 text-xs">
                <ShoppingBag size={14} strokeWidth={1.5} /> Store
              </Link>
              {me ? (
                <div className="hidden sm:flex items-center gap-1">
                  <Link
                    to="/admin"
                    data-testid="nav-admin-dash"
                    title={`Admin dashboard (${me.email})`}
                    className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full bg-neutral-900 text-white text-xs hover:bg-black transition-colors"
                  >
                    <span className="w-6 h-6 rounded-full bg-[--sanctum-accent] text-neutral-900 flex items-center justify-center text-[11px] font-semibold">
                      {initial}
                    </span>
                    Admin
                  </Link>
                  <button
                    data-testid="nav-signout"
                    onClick={signOut}
                    title="Sign out"
                    className="p-2 rounded-full text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100"
                  >
                    <LogOut size={14} strokeWidth={1.5} />
                  </button>
                </div>
              ) : (
                <Link
                  to="/admin/login"
                  data-testid="nav-admin-link"
                  className="hidden sm:inline-flex items-center btn-pill btn-outline !py-2 !px-4 text-xs"
                  title="Admin sign in"
                >
                  Admin
                </Link>
              )}
            </div>
          </div>
        </header>
      )}

      <main className={hideChrome ? "flex-1" : "flex-1 pt-16 pb-32"}>{children}</main>

      {!hideChrome && (
        <footer className="border-t border-[--sanctum-border] bg-white/50">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid md:grid-cols-4 gap-10">
            <div>
              <div className="font-display text-2xl mb-3">Sanctum</div>
              <p className="text-sm text-neutral-500 leading-relaxed">
                Serene Christian music. Distraction-free listening, honest pricing, art at the center.
              </p>
            </div>
            <div>
              <div className="eyebrow mb-4">Explore</div>
              <ul className="space-y-2 text-sm">
                <li><Link to="/songs" className="hover:text-neutral-900 text-neutral-500">Songs</Link></li>
                <li><Link to="/videos" className="hover:text-neutral-900 text-neutral-500">Videos</Link></li>
                <li><Link to="/albums" className="hover:text-neutral-900 text-neutral-500">Albums</Link></li>
                <li><Link to="/library" className="hover:text-neutral-900 text-neutral-500">My Library</Link></li>
              </ul>
            </div>
            <div>
              <div className="eyebrow mb-4">Company</div>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:text-neutral-900 text-neutral-500">About</Link></li>
                <li><Link to="/contact" className="hover:text-neutral-900 text-neutral-500">Contact</Link></li>
                <li><Link to="/store" className="hover:text-neutral-900 text-neutral-500">Store</Link></li>
              </ul>
            </div>
            <div>
              <div className="eyebrow mb-4">Pricing</div>
              <ul className="space-y-2 text-sm text-neutral-500">
                <li>MP3 · <span className="text-neutral-900">$1.99</span></li>
                <li>MP4 · <span className="text-neutral-900">$2.99</span></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-[--sanctum-border] py-6 flex items-center justify-between px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="text-xs text-neutral-400 tracking-wider">
              © {new Date().getFullYear()} Sanctum Music. Made with reverence.
            </div>
            <Link
              to="/admin/login"
              data-testid="footer-admin-link"
              className="text-[11px] tracking-[0.28em] uppercase text-neutral-400 hover:text-neutral-800 transition-colors"
            >
              Admin
            </Link>
          </div>
        </footer>
      )}

      {!hideChrome && <StickyPlayer />}
      <Toaster position="top-center" richColors />
    </div>
  );
}
